import type { AsyncSequence } from "../types/AsyncSequence"

/**
 * Wraps an async iterable to catch errors without throwing.
 * Returns both a safe stream and a promise that resolves to any error encountered.
 *
 * @param stream - The async sequence to wrap
 * @returns Object containing a safe stream and an error promise
 *
 * @example
 * ```ts
 * import { SafeIterable } from "@pdfking/tfunc"
 *
 * async function* brokenIterable() {
 *   yield 1;
 *   yield 2;
 *   throw new Error("Something went wrong");
 * }
 *
 * const { stream, error } = SafeIterable(brokenIterable());
 *
 * const results = await IterableToArray(stream); // [1, 2]
 * const errorResult = await error; // Error: Something went wrong
 *
 * if (errorResult) {
 *   console.error('Caught error:', errorResult.message);
 * }
 * ```
 */
export const SafeIterable = <T>(
  stream: AsyncSequence<T>
): {
  error: Promise<Error | undefined>
  stream: AsyncGenerator<T>
} => {
  let resolve: (value: Error | undefined) => void
  const error: Promise<Error | undefined> = new Promise(r => (resolve = r))

  const generator = async function* () {
    try {
      for await (const item of stream) {
        yield item
      }
    } catch (err) {
      return resolve(err instanceof Error ? err : new Error(String(err)))
    }
    return resolve(undefined)
  }

  return {
    error,
    stream: generator(),
  }
}
